import { apiWrapper } from "./apiWrapper";

/**
 * Applicant roster + signing progress for an order.
 *
 * Identity is `applicant_uid` (stable across reorder/delete); `applicant_index`
 * is display order only. See PRD D6/D14.
 */
export interface OrderApplicant {
  id: number;
  applicant_uid: string;
  applicant_index: number;
  first_name: string | null;
  last_name: string | null;
  name: string;
  email: string | null;
  is_main: boolean;
  fields_signed: number;
  fields_total: number;
  percent: number;
  status: "pending" | "in_progress" | "completed";
}

export interface OrderApplicantsResponse {
  order_id: number;
  /** false for legacy orders — the roster/signing UI stays hidden. */
  enabled: boolean;
  all_complete: boolean;
  applicants: OrderApplicant[];
}

export const orderApplicantApi = {
  /** Roster + progress. Returns [] when the order has no roster. */
  list: async (orderId: number): Promise<OrderApplicant[]> => {
    const res = await apiWrapper<OrderApplicantsResponse>(
      `/functions/v1/order-applicants?order_id=${orderId}`
    );
    return res?.applicants ?? [];
  },

  /** Full response, including the `enabled` snapshot and `all_complete`. */
  get: async (orderId: number): Promise<OrderApplicantsResponse> =>
    await apiWrapper<OrderApplicantsResponse>(
      `/functions/v1/order-applicants?order_id=${orderId}`
    ),

  /** Upsert the roster. Preserve applicant_uid on existing rows. */
  upsert: async (payload: {
    order_id: number;
    filler_is_applicant?: boolean;
    applicants: Array<{
      applicant_uid?: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      date_of_birth?: string | null;
      married?: boolean;
      filling_on_behalf?: boolean;
      on_behalf_of_name?: string | null;
    }>;
  }) =>
    await apiWrapper(`/functions/v1/order-applicants`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Mint a token and email the signing link to this applicant. */
  invite: async (applicantId: number) =>
    await apiWrapper(`/functions/v1/order-applicants/${applicantId}/invite`, {
      method: "POST",
    }),

  /** Fresh shareable link (for copy / SMS). Rotates the previous token. */
  getLink: async (
    applicantId: number
  ): Promise<{ url: string; expires_at: string; field_count: number }> =>
    await apiWrapper(`/functions/v1/order-applicants/${applicantId}/link`, {
      method: "POST",
    }),
};

export default orderApplicantApi;
